import { useTranslation } from '@frontend/i18n';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, PopoverMenuItem } from '@frontend/design-system';
import { useInboxTaskStatusLabels } from '../../../stores/inbox-store';
import { InboxTaskStatus } from '../../../types';
import { FilterItemPopoverCheckboxField } from '../filter-item-popover-checkbox-field';
import { GetItemPropsFn, GetMenuPropsFn } from '../types';

type TasksFilterPopoverMenuProps = {
  getMenuProps: GetMenuPropsFn;
  getItemProps: GetItemPropsFn;
  groupIds?: string[];
  toggleItems: (taskStatuses: InboxTaskStatus[]) => void;
  selectedTasks: InboxTaskStatus[];
};

export const TasksFilterPopoverMenu = ({
  getMenuProps,
  getItemProps,
  toggleItems,
  selectedTasks,
}: TasksFilterPopoverMenuProps): ReturnType<typeof PopoverMenu> => {
  const { t } = useTranslation('inbox');
  const taskStatusLabels = useInboxTaskStatusLabels();

  const availableTaskStatuses = [InboxTaskStatus.TODO, InboxTaskStatus.IN_PROGRESS];

  const hasAllSelected = availableTaskStatuses.every((status) => selectedTasks.includes(status));
  const hasNoneSelected = selectedTasks.length === 0;

  const handleSelectAll = () => {
    const unselectedStatuses = availableTaskStatuses.filter((status) => !selectedTasks.includes(status));
    toggleItems(unselectedStatuses);
  };

  const handleClear = () => {
    toggleItems(selectedTasks);
  };

  return (
    <PopoverMenu {...getMenuProps()} css={{ minWidth: 180 }}>
      <PopoverMenuItem
        as='span'
        {...getItemProps({
          index: 0,
          disableCloseOnSelect: true,
        })}
        css={{
          marginTop: theme.spacing(1),
          padding: theme.spacing(1, 0.75),
          borderBottom: `1px solid ${theme.colors.neutral20}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ':hover': {
            backgroundColor: 'inherit',
          },
          ':focus': {
            backgroundColor: 'inherit',
          },
        }}
      >
        <Button
          variant='tertiary'
          css={{
            color: theme.font.colors.primary,
            fontWeight: theme.font.weight.semibold,
          }}
          onClick={handleSelectAll}
          disabled={hasAllSelected}
          trackingId={`${InboxPrefixes.List}-filters-popover-tasks-select-all`}
        >
          {t('Select All')}
        </Button>
        <Button
          variant='tertiary'
          css={{
            color: theme.font.colors.primary,
            fontWeight: theme.font.weight.semibold,
          }}
          onClick={handleClear}
          disabled={hasNoneSelected}
          trackingId={`${InboxPrefixes.List}-filters-popover-tasks-clear`}
        >
          {t('Clear')}
        </Button>
      </PopoverMenuItem>
      <div
        css={{
          maxHeight: 240,
          overflowY: 'auto',
        }}
      >
        {availableTaskStatuses.map((taskStatus, index) => (
          <PopoverMenuItem
            key={taskStatus}
            {...getItemProps({
              index: index + 1,
              disableCloseOnSelect: true,
              onClickCapture: (e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleItems([taskStatus]);
              },
            })}
            css={{
              width: '100%',
              cursor: 'pointer',
              '*': {
                cursor: 'pointer',
              },
            }}
          >
            <FilterItemPopoverCheckboxField
              isChecked={selectedTasks.includes(taskStatus)}
              name={`task-${taskStatus}`}
              label={taskStatusLabels[taskStatus]}
            />
          </PopoverMenuItem>
        ))}
      </div>
    </PopoverMenu>
  );
};
